/**
 * Sanitize class for sanitizing HTML strings based on a configurable set of rules.
 */
import sanitizeHtml from 'sanitize-html';
class Sanitize {
    /**
     * Constructs a new Sanitize instance with a given configuration.
     * @param {Object} config - The configuration object to customize the sanitization rules.
     */
    constructor(config) {
        const _defaultConfig = {
            allowedTags: [
                'p',
                'span',
                'strong',
                'em',
                'h1', 'h2', 'h3', 'h4', 'h5',
                'pre'
            ],
            allowedAttributes: {
                '*': ['class', 'id', 'style']
            },
            selfClosing: ['br', 'hr'],
            allowedSchemes: false,
            allowedSchemesByTag: {},
            allowProtocolRelative: false,
            allowedIframeHostnames: false
        };

        this._config = { ..._defaultConfig };
        if (config) {
            this._config = { ...this._config, ...config };
        }
    }

    /**
     * Sanitizes an HTML string based on the current configuration.
     * @param {string} htmlStr - The HTML string to be sanitized.
     * @returns {string} The sanitized HTML string.
     */
    sanitizeHtml(htmlStr) {
        return sanitizeHtml(htmlStr, this._config);
    }

    /**
     * Enables all HTML tags and attributes by clearing the allowed tags and attributes lists.
     * SMELL: This method does not differentiate between tags and attributes, potentially allowing unsafe elements.
     */
    enableAllTags() {
        this._config.allowedTags = [];
        this._config.allowedAttributes = {};
    }

    /**
     * Disables all HTML tags and attributes by clearing the allowed tags and attributes lists.
     * SMELL: This method does not differentiate between tags and attributes, potentially removing necessary elements.
     */
    disableAllTags() {
        this._config.allowedTags = [];
        this._config.allowedAttributes = {};
    }

    /**
     * Applies a simple transformation to the HTML.
     * @param {string} newTag - The new tag to replace existing tags.
     * @param {Object} newAttributes - The new attributes to apply.
     * @param {boolean} shouldMerge - Whether to merge attributes with existing ones.
     * @returns {Function} A function that performs the transformation.
     */
    simpleTransform(newTag, newAttributes, shouldMerge) {
        return sanitizeHtml.simpleTransform(newTag, newAttributes, shouldMerge);
    }

    /**
     * Retrieves the current configuration used for sanitization.
     * @returns {Object} The current configuration object.
     */
    getConfig() {
        return this._config;
    }
}

export default Sanitize;